import { z } from 'zod';

/**
 * Normalize any phone-like input to a clean `+<digits>` form.
 * No country restriction — this is a Georgian store that ships within Georgia and
 * internationally, so we accept any reasonable number (Georgian +995, Russian +7, etc.).
 * Never throws: unparseable input is returned trimmed so the buyer is never blocked.
 */
export function normalizePhone(raw: string): string | undefined {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const digits = trimmed.replace(/\D/g, '');
    return digits.length >= 6 ? `+${digits}` : trimmed;
}

export const createOrderFromDraftSchema = z
    .object({
        draftId: z.string().uuid(),

        addressId: z.string().uuid().optional(),
        city: z.string().min(1, 'This field is required').optional(),
        address: z.string().min(3, 'This field is required').optional(),

        fullName: z.string().min(3, 'Please enter your full name'),
        email: z
            .string({ required_error: 'This field is required' })
            .min(3, 'This field is required')
            .email('Invalid email address'),
        phone: z
            .string()
            .optional()
            .transform(v => normalizePhone(v ?? '')),
    })
    .superRefine((data, ctx) => {
        const hasId = !!data.addressId;
        const hasInline = !!data.city && !!data.address;
        if (!hasId && !hasInline) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please provide an address: pick a saved one or enter city and address',
                path: ['address'],
            });
        }

        if (!data.email && !data.phone) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Please provide an email or a phone number',
                path: ['email'],
            });
        }
    });

export type CreateOrderFromDraftSchema = z.infer<typeof createOrderFromDraftSchema>;
